import { jwtDecode } from "jwt-decode";

const BACKEND_URI = "";

import {
  ChatAppResponse,
  ChatAppResponseOrError,
  ChatAppRequest,
  Config,
  SimpleAPIResponse,
} from "./models";
import {HistoryElement} from "../components/ChatHistory";

import { FeedbackPayload } from "../components/Answer/AnswerFeedback"; // adjust path as needed

/**
 * Generates a headers object for authorization purposes based on the provided ID token.
 * If login is used and app services login is not used, the ID token will be included in the headers.
 *
 * @param {string | undefined} idToken - The ID token for the logged-in account.
 * @return {Promise<Record<string, string>>} A Promise that resolves to an object containing the authorization headers.
 * Returns an empty object if login conditions are not met or if the ID token is not provided.
 */
export async function getHeaders(forceRefresh = false): Promise<Record<string, string>> {
    let idToken: string | undefined = await getToken(forceRefresh)
    // If using login and not using app services, add the id token of the logged in account as the authorization
    if (idToken) {
        return {Authorization: `Bearer ${idToken}`};
    }


    return {};
}

interface AppServicesToken {
    id_token: string;
    access_token: string;
    user_claims: Record<string, any>;
    expires_on: string;
}


const appServicesAuthTokenUrl = ".auth/me";
const appServicesAuthTokenRefreshUrl = ".auth/refresh";

// Seconds before the real `exp` at which we already treat the token as
// expired. Covers client/server clock skew and the request's in-flight time,
// so a token that is valid at send time can't expire before the backend reads
// it — the main cause of the intermittent chat 401.
const TOKEN_EXPIRY_MARGIN_SECONDS = 120;

// Clears the cached chat token so the next getToken() re-fetches from Easy Auth.
// Called by the retry-on-401 path in chatApi/askApi.
export function clearCachedToken(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('expires_on');
}

async function getToken(forceRefresh = false): Promise<string> {


    const fetchToken = (): Promise<AppServicesToken | null> => {


        return fetch(appServicesAuthTokenUrl).then(r => {
            if (r.ok) {
                return r.json().then(json => {
                    if (json.length > 0) {
                        return {
                            id_token: json[0]["id_token"] as string,
                            access_token: json[0]["access_token"] as string,
                            user_claims: json[0]["user_claims"].reduce((acc: Record<string, any>, item: Record<string, any>) => {
                                acc[item.typ] = item.val;
                                return acc;
                            }, {}) as Record<string, any>,
                            expires_on: json[0]["expires_on"] as string
                        } as AppServicesToken;
                    }

                    return null;
                });
            }

            return null;
        });
    }

    // Ask Easy Auth to refresh the underlying session token, then re-read it.
    // Mirrors the pattern in authConfig.ts. Best-effort: on any failure we fall
    // back to a plain fetchToken().
    const refreshAndFetchToken = async (): Promise<AppServicesToken | null> => {
        try {
            await fetch(appServicesAuthTokenRefreshUrl);
        } catch {
            // ignore — fall through to a normal fetch attempt
        }
        return fetchToken();
    }

    function isTokenExpired(token: string | null | undefined): boolean {
        if (token === null || token === undefined || token === "undefined") {
            return true;
        }
        if (!isJwt(token)) {
            return true;
        }
        try {
            const decodedToken: { exp: number } = jwtDecode(token);
            const currentTime = Math.floor(Date.now() / 1000);
            // Refresh proactively before the real expiry (skew + in-flight buffer).
            return decodedToken.exp - TOKEN_EXPIRY_MARGIN_SECONDS < currentTime;
        } catch {
            return true;
        }
    }

    function isJwt(token: string | null | undefined): boolean {
        if (!token) return false;
        // A JWT has 3 base64url-encoded parts separated by dots
        const parts = token.split(".");
        return parts.length === 3;
    }

    function getBestToken(authResponse: AppServicesToken | null | undefined): string | null {
        if (!authResponse) return null;
        // Prefer access_token if it's a JWT (Microsoft Entra)
        // Fall back to id_token for providers like Salesforce where access_token is opaque
        if (isJwt(authResponse.access_token)) {
            return authResponse.access_token;
        }
        if (isJwt(authResponse.id_token)) {
            return authResponse.id_token;
        }
        return authResponse.access_token ?? null;
    }

    function updateLocalStorage(authResponse: AppServicesToken | null | undefined): void {
        const token = getBestToken(authResponse);
        if (token) {
            localStorage.setItem('authToken', token);
        }
        if (authResponse?.expires_on) {
            localStorage.setItem('expires_on', authResponse.expires_on);
        }
    }

    // Force path (used by retry-on-401): skip the cache, ask Easy Auth to
    // refresh the session, then re-read the token.
    if (forceRefresh) {
        clearCachedToken();
        const authResponse = await refreshAndFetchToken();
        const refreshed = getBestToken(authResponse);
        updateLocalStorage(authResponse);
        return refreshed ?? "";
    }

    let authToken = localStorage.getItem('authToken');

    if (isTokenExpired(authToken)) {
        // First try a plain re-read from Easy Auth.
        let authResponse = await fetchToken();
        let candidate = getBestToken(authResponse);
        // If that still yields no usable token (session lapse / transient
        // failure), ask Easy Auth to refresh the session and try once more.
        if (isTokenExpired(candidate)) {
            authResponse = await refreshAndFetchToken();
            candidate = getBestToken(authResponse);
        }
        authToken = candidate;
        updateLocalStorage(authResponse);
    }

    return authToken ?? "";
}

/**
 * Fetches and returns configuration settings from the backend API.
 *
 * @return {Promise<Config>} A promise that resolves to the configuration settings.
 */
export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET",
    });

    return (await response.json()) as Config;
}

/**
 * Sends a request to the backend API to process a chat app request and returns the response.
 *
 * @param {ChatAppRequest} request - The request object containing the data to be sent to the API.
 * @param {string | undefined} idToken - An optional identity token used for authentication.
 * @return {Promise<ChatAppResponse>} - A promise that resolves to the response from the API, if successful.
 * @throws {Error} - Throws an error if the request fails or if the API returns an error.
 */
export async function askApi(
    request: ChatAppRequest,
    idToken: string | undefined
): Promise<ChatAppResponse> {
    let headers = await getHeaders();
    let response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify(request),
    });
    // Same intermittent-401 self-heal as chatApi: force a token refresh and
    // retry once before surfacing the error.
    if (response.status === 401) {
        clearCachedToken();
        headers = await getHeaders(true);
        response = await fetch(`${BACKEND_URI}/ask`, {
            method: "POST",
            headers: {...headers, "Content-Type": "application/json"},
            body: JSON.stringify(request),
        });
    }

    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }

    return parsedResponse as ChatAppResponse;
}

/**
 * Sends a chat request to the backend API and returns the server's response.
 *
 * @param {ChatAppRequest} request - The chat request object containing the necessary data for processing the chat.
 * @param {boolean} shouldStream - A boolean indicating whether the response should be streamed.
 * @param {string | undefined} idToken - An optional ID token for authentication.
 * @return {Promise<Response>} A promise that resolves to the server's response.
 */
export async function chatApi(
    request: ChatAppRequest,
    shouldStream: boolean,
    idToken: string | undefined
): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    let headers = await getHeaders();
    let response = await fetch(url, {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify(request),
    });
    // A 401 here almost always means the Authorization header was missing or
    // stale at send time (Easy Auth session lapse / near-expiry token). Force a
    // token refresh and retry exactly once so the user never sees the error.
    if (response.status === 401) {
        clearCachedToken();
        headers = await getHeaders(true);
        response = await fetch(url, {
            method: "POST",
            headers: {...headers, "Content-Type": "application/json"},
            body: JSON.stringify(request),
        });
    }
    return response;
}

/**
 * Sends a request to the speech synthesis API with the given text and returns
 * a URL pointing to the synthesized speech audio.
 *
 * @param {string} text - The text to be converted into speech using the API.
 * @return {Promise<string | null>} A promise that resolves to a URL pointing to the audio blob
 * or null if the request fails or speech synthesis is not enabled.
 */
export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            text: text,
        }),
    })
        .then((response) => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then((blob) => (blob ? URL.createObjectURL(blob) : null));
}

/**
 * Generates the file path for a given citation.
 *
 * @param {string} citation - The citation for which the file path is to be generated.
 * @param {string} page - The page number to navigate to in the document.
 * @return {string} The complete file path for the specified citation with page number.
 */
export function getCitationFilePath(citation: string, page?: string): string {
    const baseUrl = `${BACKEND_URI}/content/${citation}`;
    return page ? `${baseUrl}#page=${page}` : baseUrl;
}

/**
 * Uploads a file to the server using a POST request.
 *
 * @param {FormData} request - The FormData object containing the file and any additional data to be uploaded.
 * @param {string} idToken - The ID token used for authentication in the request headers.
 * 
 * @return {Promise<SimpleAPIResponse>} A promise that resolves to the server's response represented as a SimpleAPIResponse object.
 * @throws {Error} If the server returns a non-OK status, an error is thrown with the status text.
 */
export async function uploadFileApi(
    request: FormData,
    idToken: string
): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: await getHeaders(),
        body: request,
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

/**
 * Deletes an uploaded file from the server.
 *
 * @param {string} filename - The name of the file to be deleted.
 * @param {string} idToken - The authentication token for user validation.
 * @return {Promise<SimpleAPIResponse>} A promise that resolves to the server's response after attempting to delete the file.
 * @throws {Error} Throws an error if the deletion fails, with an appropriate message from the server response.
 */
export async function deleteUploadedFileApi(
    filename: string,
    idToken: string
): Promise<SimpleAPIResponse> {
    const headers = await getHeaders();
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify({filename}),
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

/**
 * Fetches a list of uploaded files from the server.
 *
 * @param {string} idToken - The token used for authorizing the request.
 * @return {Promise<string[]>} A promise that resolves to an array of filenames.
 * @throws Will throw an error if the server response is not okay.
 */
export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: await getHeaders(),
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

/**
 * Generates and saves a chat label directly to the database.
 * This is a fire-and-forget operation - errors are logged but not thrown.
 *
 * @param {string} firstUserMessage - The first message from the user to generate a label from.
 * @param {string} sessionId - The session ID to associate the label with.
 * @param {string | undefined} idToken - The identity token used for authentication.
 * @return {Promise<void>} A promise that resolves when the request completes (success or failure).
 */
export async function generateLabel(
    firstUserMessage: string,
    sessionId: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders();

    try {
        await fetch(`${BACKEND_URI}/generateLabel`, {
            method: "POST",
            headers: {...headers, "Content-Type": "application/json"},
            body: JSON.stringify({ firstUserMessage, sessionId }),
        });
        // Fire-and-forget - we don't wait for or check the response
    } catch (error) {
        console.error("Error generating label:", error);
        // Don't throw - this is a background operation
    }
}

/**
 * Saves the provided object to the user's history in the backend.
 *
 * @param {object} object - The data object to be saved to history (should include sessionId, chatContext, and label).
 * @param {string | undefined} idToken - The identity token used for authentication. If undefined, no authorization will be applied.
 * @return {Promise<Response>} A promise that resolves to the Response from the backend server, indicating the result of the save operation.
 */
export async function saveToHistory(
    object: object,
    idToken: string | undefined  // Legacy parameter - not used, getHeaders() handles auth internally
): Promise<Response> {
    try {
        // Validate JSON serializability before sending
        let requestBody: string;
        try {
            requestBody = JSON.stringify(object);
            // Verify it can be parsed back (catches circular references, undefined values, etc.)
            JSON.parse(requestBody);

        } catch (jsonError) {
            console.error('❌ [saveToHistory] JSON serialization failed', {
                error: jsonError instanceof Error ? jsonError.message : String(jsonError),
                objectKeys: Object.keys(object),
                objectType: typeof object
            });
            throw new Error(`Invalid object structure: ${jsonError instanceof Error ? jsonError.message : 'Cannot serialize to JSON'}`);
        }
        
        const headers = await getHeaders();
        // Print JSON body for debugging (truncated if too large)        

        const response = await fetch(`${BACKEND_URI}/saveToHistory`, {
            method: "POST",
            headers: {...headers, "Content-Type": "application/json"},
            body: requestBody,
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ [saveToHistory] HTTP error', {
                status: response.status,
                statusText: response.statusText,
                bodyLength: errorText.length,
                bodyPreview: errorText.substring(0, 500),
                isHTML: errorText.trim().startsWith('<')
            });
        }
        
        return response;
    } catch (error) {
        console.error('[saveToHistory] EXCEPTION caught in API call', {
            error,
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        });
        throw error;
    }
}

/**
 * Makes a POST request to the server to retrieve historical data for a user.
 *
 * @param {string | undefined} idToken - The ID token used for authorization. It can be undefined if no token is available.
 * @return {Promise<Response>} - A promise that resolves to the server's response containing the user's historical data in JSON format.
 */
export async function getFromHistory(
    idToken: string | undefined
): Promise<HistoryElement[]> {
    try {
        const headers = await getHeaders();       
        
        const response = await fetch(`${BACKEND_URI}/getFromHistory`, {
            method: "POST",
            headers: {...headers, "Content-Type": "application/json"},
            body: JSON.stringify({}),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();

        
        return data;
    } catch (error) {
        
        throw error;
    }
}

/**
 * Removes an entry from the history based on the provided identifiers.
 *
 * @param {string | undefined} idToken - The identification token used to authenticate the request.
 * @param {string | undefined} userId - The unique identifier of the user whose history entry is to be removed.
 * @param {string | undefined} sessionId - The session identifier for the history entry to be removed.
 * @return {Promise<Response>} A promise that resolves to the server's response.
 */
export async function removeFromHistory(
    idToken: string | undefined,
    userId: string | undefined,
    sessionId: string | undefined
): Promise<Response> {
    const headers = await getHeaders();

    return await fetch(`${BACKEND_URI}/removeFromHistory`, {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify({
            userId: userId,
            sessionId: sessionId,
        }),
    });
}

// function for creating Custom Syytem(Prompt) Templates
export interface SystemTemplate {
  id?: string; // Optional: Template identifier
  name: string; // Template identifier/name
  template_content: string; // The actual template content
  system_message: string; // The system message to be displayed
}

export async function getSystemTemplates(
  idToken: string | undefined
): Promise<Response> {
  const headers = await getHeaders();
  return await fetch(`${BACKEND_URI}/getSystemTemplates`, {
    method: "GET",
    headers,
  });
}

export async function deleteSystemTemplate(
  templateId: string,
  idToken: string | undefined
): Promise<Response> {
  const headers = await getHeaders();
  return await fetch(`${BACKEND_URI}/deleteSystemTemplate/${templateId}`, {
    method: "DELETE",
    headers,
  });
}

export async function createSystemTemplate(
  template: SystemTemplate,
  idToken: string | undefined
): Promise<Response> {
  const headers = await getHeaders();

  return await fetch(`${BACKEND_URI}/createSystemTemplate`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(template),
  });
}

export async function initializeSemanticRouter(
  idToken: string | undefined
): Promise<Response> {
  const headers = await getHeaders();

  return await fetch(`${BACKEND_URI}/initializeSemanticRouter`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
  });
}

/**
 * Sends user feedback to the backend `/feedback` route.
 *
 * @param {FeedbackPayload & { quetion: string }} feedback - An object containing the feedback rating, category, remark, and the related question.
 * @param {string | undefined} idToken - Optional ID token for authenticating the request.
 * @returns {Promise<Response>} A promise that resolves to the server's response.
 */

export async function feedbackApi(
    feedback: FeedbackPayload & { question: string; sessionId: string; searchLink?: string },
    idToken: string | undefined
): Promise<Response> {
    const headers = await getHeaders();

    return await fetch(`${BACKEND_URI}/feedback`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(feedback),
    });
}

/**
 * Deletes user feedback from the backend `/feedback` route based on sessionId and question.
 *
 * @param {string} sessionId - The session ID of the feedback.
 * @param {string} question - The original user question the feedback is attached to.
 * @param {string | undefined} idToken - Optional ID token for authenticating the request.
 * @returns {Promise<Response>} A promise that resolves to the server's response.
 */
export async function deleteFeedbackApi(
    sessionId: string,
    question: string,
    idToken: string | undefined
): Promise<Response> {
    const headers = await getHeaders();

    return await fetch(
        `${BACKEND_URI}/feedback?sessionId=${sessionId}&question=${encodeURIComponent(question)}`,
        {
            method: "DELETE",
            headers: { ...headers },
        }
    );
}


/**
 * Retrieves feedback data from the backend API for a date range.
 *
 * @param idToken - Auth token (currently unused; auth headers come from getHeaders).
 * @param startDate - Optional ISO date "YYYY-MM-DD" (UTC). Defaults to 7 days ago server-side.
 * @param endDate - Optional ISO date "YYYY-MM-DD" (UTC). Defaults to today server-side.
 * @return Server's response containing positive/negative/search feedback + daily counts.
 */
export async function getAllFeedbackApi(
    idToken: string | undefined,
    startDate?: string,
    endDate?: string,
    env?: string,
): Promise<Response> {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (env) params.set("env", env);
    const query = params.toString() ? `?${params.toString()}` : "";

    return await fetch(`${BACKEND_URI}/feedback${query}`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });
}

/**
 * Sends a POST request to save a given object in the library.
 *
 * @param {object} object - The object to be saved in the library.
 * @param {string | undefined} idToken - The ID token used for authentication. It can be undefined if no authentication is needed.
 * @return {Promise<Response>} A promise that resolves to the response of the fetch request.
 */
export async function saveInLibrary(
    object: object,
    idToken: string | undefined
): Promise<Response> {
    const headers = await getHeaders();

    return await fetch(`${BACKEND_URI}/saveInLibrary`, {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify(object),
    });
}

/**
 * Fetches data from the library backend service.
 *
 * @param {string | undefined} idToken - The ID token used for authentication. It can be a string or undefined.
 * @return {Promise<Response>} A promise that resolves to the server's response object.
 */
export async function getFromLibrary(
    idToken: string | undefined
): Promise<Response> {
    const headers = await getHeaders();


    return await fetch(`${BACKEND_URI}/getFromLibrary`, {
        method: "POST",
        headers: {...headers, "Content-Type": "application/json"},
        body: JSON.stringify({}),
    });
}

// Add this interface near the top of the file with other interfaces
interface SearchFacet {
    value: string;
    count: number;
}

interface SearchFacetsResponse {
    status: 'success' | 'error';
    facets: {
        [key: string]: SearchFacet[];
    };
    message?: string;
}

/**
 * Retrieves search facets from the backend API
 *
 * @param {Record<string, string[]>} selectedFilters - The currently selected filters to apply.
 * @param {string} searchTerm - The search term to apply.
 * @return {Promise<SearchFacetsResponse>} A promise that resolves to the search facets
 */
export async function getSearchFacetsApi(
    selectedFilters: Record<string, string[]>, 
    searchTerm: string = "",
    facetFilter?: { facetName: string, prefix: string }
): Promise<SearchFacetsResponse> {
    const requestPayload = {
        selectedFilters: selectedFilters, 
        searchTerm: searchTerm,
        facetFilter: facetFilter
    };
   
    
    const headers = await getHeaders();
    try {
        const requestBody = JSON.stringify(requestPayload);
        
        const response = await fetch(`${BACKEND_URI}/search/facets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: requestBody
        });
        
        // Check if response is OK before parsing
        if (!response.ok) {
            const responseText = await response.text();
          
            throw new Error(`HTTP ${response.status}: ${response.statusText} - ${responseText.substring(0, 100)}`);
        }
        
        const data = await response.json();
        return data as SearchFacetsResponse;
    } catch (error) {
        
        throw error;
    }
}

/**
 * Search document result interface
 */
export interface SearchDocument {
    id: string;
    sourcefile: string;
    sourcefile_id?: string;
    sourcepage?: string;
    category?: string;
    documenttype?: string;
    storageUrl?: string;
    originUrl?: string;
    folderlink?: string;
    sourcesystem?: string;
    hasEmbedding?: boolean; // Indicates if document has vector embeddings (searchable in chat)
    product?: string; // Product name (from Product or newProduct field)
    machinenumber?: string; // Machine number
    lastModifiedDateTime?: string; // Last modified date/time
    docsize?: number; // Document size in bytes
    documentformat?: string; // File extension
    sitename?: string; // SharePoint site name
    eseApproval?: boolean; // ESE approval status
    vendorApproval?: boolean; // Vendor approval status
}

/**
 * Search documents response interface
 */
export interface SearchDocumentsResponse {
    status: string;
    documents: SearchDocument[];
    total: number;
    distinctCount?: number;
    page?: number;
    totalPages?: number;
    limit?: number;
    message?: string;
}

/**
 * Search for distinct documents based on filters with pagination.
 * Returns paginated unique documents matching the filter criteria.
 *
 * @param {Record<string, string[]>} selectedFilters - The filters to apply.
 * @param {number} limit - Maximum number of documents per page (default: 10).
 * @param {number} page - Page number (1-indexed, default: 1).
 * @param {string} searchTerm - Optional search term to filter by document name.
 * @return {Promise<SearchDocumentsResponse>} A promise that resolves to the search results.
 */
export async function searchDocumentsApi(
    selectedFilters: Record<string, string[]>,
    limit: number = 10,
    page: number = 1,
    searchTerm: string = "",
    signal?: AbortSignal
): Promise<SearchDocumentsResponse> {
    const headers = await getHeaders();

    try {
        const response = await fetch(`${BACKEND_URI}/search/documents`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: JSON.stringify({
                selectedFilters,
                limit,
                page,
                searchTerm
            }),
            signal
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
        }
        
        return await response.json() as SearchDocumentsResponse;
    } catch (error) {
        console.error("❌ [searchDocumentsApi] Error:", error);
        throw error;
    }
}

/**
 * Checks if the currently authenticated user has admin access.
 *
 * @param {string} idToken - The ID token used for authentication in the request headers.
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether the user is an admin.
 * @throws {Error} If the server returns a non-OK status, an error is thrown with the status text.
 */
export async function checkAdminAccessApi(idToken: string): Promise<boolean> {
    const response = await fetch(`${BACKEND_URI}/check-admin`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Admin check failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.isAdmin === true;
}


export const updateFeedbackStatus = async (feedbackId: string, status: "pending" | "in progress" | "completed") => {
    const res = await fetch(`${BACKEND_URI}/feedback/${feedbackId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });

    if (!res.ok) {
      throw new Error(`Failed to update feedback status: ${res.statusText}`);
    }

    return res.json(); // returns { status: "success", newStatus: "..." }
  };


// =============================================================================
// External Document Sharing
// =============================================================================

export type ShareRecipient = {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    phone: string;
};

export type ShareFlags = {
    nda: boolean;
    dpa: boolean;
    // downloadAllowed lives on the persisted share row (returned from list/meta)
    // but is NOT settable from the client — the backend derives it from the
    // SharePoint ApprovalDownload column at share-create time.
    downloadAllowed?: boolean;
};

export type CreateSharePayload = {
    sharepointUrl: string;
    recipient: ShareRecipient;
    flags: ShareFlags;
    expiryDays: 1 | 3 | 7;
};

export type CreateShareResponse = {
    token: string;
    shareId: string;
    expiresAt: number;
    shareUrl: string;
};

export async function getShareSharepointMetadataApi(sharepointUrl: string): Promise<{
    filename: string;
    // null when the SharePoint column couldn't be read; treated as "not allowed".
    downloadAllowed: boolean | null;
}> {
    const headers = await getHeaders();
    const res = await fetch(`${BACKEND_URI}/api/share/sharepoint-metadata?url=${encodeURIComponent(sharepointUrl)}`, {
        method: "GET",
        headers,
    });
    if (!res.ok) {
        throw new Error(`SharePoint metadata fetch failed: ${res.status}`);
    }
    return res.json();
}

export async function createShareApi(payload: CreateSharePayload): Promise<CreateShareResponse> {
    const headers = await getHeaders();
    const res = await fetch(`${BACKEND_URI}/api/share`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.text();
        throw new Error(`Share creation failed: ${res.status} ${body}`);
    }
    return res.json();
}

export type ShareListItem = {
    id: string;
    senderEmail: string | null;
    senderOid: string | null;
    recipient: ShareRecipient | null;
    filename: string | null;
    flags: ShareFlags | null;
    createdAt: number;
    expiresAt: number;
    revoked: boolean;
    revokedAt: number | null;
    accessCount: number;
};

export async function listSharesApi(): Promise<{ items: ShareListItem[] }> {
    const headers = await getHeaders();
    const res = await fetch(`${BACKEND_URI}/api/share/list`, { method: "GET", headers });
    if (!res.ok) {
        throw new Error(`listShares failed: ${res.status}`);
    }
    return res.json();
}

export async function revokeShareApi(shareId: string): Promise<void> {
    const headers = await getHeaders();
    const res = await fetch(`${BACKEND_URI}/api/share/${encodeURIComponent(shareId)}/revoke`, {
        method: "POST",
        headers,
    });
    if (!res.ok) {
        throw new Error(`revokeShare failed: ${res.status}`);
    }
}

export async function getShareMetaApi(token: string): Promise<{
    filename: string;
    contentType: string | null;
    sizeBytes: number | null;
    expiresAt: number;
    downloadAllowed: boolean;
    previewable: boolean;
    senderEmail: string;
}> {
    const res = await fetch(`${BACKEND_URI}/api/share/${encodeURIComponent(token)}/meta`);
    if (!res.ok) {
        throw new Error(`share_meta_${res.status}`);
    }
    return res.json();
}

  